import {
   Component,
   ElementRef,
   EventEmitter,
   HostListener,
   Input,
   OnDestroy,
   OnInit,
   Output,
   Renderer2,
   ViewChild,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Subscription } from "rxjs";
import { IdexService } from "../../idex.service";
import { zDoxItem } from "../../shared/types";
import { AnnotationControllerService } from "../annotation-controller/annotation-controller.service";
import { FileViewerConfig, ViewerContent, ViewerType } from "./file-viewer/file-viewer.type";
import { ViewerInfoPanelComponent } from "./viewer-info-panel/viewer-info-panel.component";
import { ViewerTopBarConfig } from "./viewer-top-bar/viewer-top-bar.component";
import { DefaultTo } from "../../shared/defaultTo.decorator";
import { InfoPanelConfig, MetadataFormProperty, MetadataFormType } from "./viewer-info-panel/viewer-info-panel.types";
import { FileViewerComponent } from "./file-viewer/file-viewer.component";
import { ActivatedRoute, Params } from "@angular/router";

@Component({
   selector: "app-viewer-controller",
   templateUrl: "./viewer-controller.component.html",
   styleUrls: ["./viewer-controller.component.css"],
})
export class ViewerControllerComponent implements OnInit, OnDestroy {
   @Input() topBarConfig: ViewerTopBarConfig;
   @Input() viewerConfig: FileViewerConfig;

   @Input() doc: zDoxItem;

   @Input() @DefaultTo("other") viewerType: ViewerType;
   @Input() viewerContent?: ViewerContent<typeof this.viewerType>;
   @Input() error: unknown;

   // TOP-BAR OUTPUTS
   @Output() clickBack = new EventEmitter<void>();
   @Output() clickShare = new EventEmitter<void>();
   @Output() clickStartFlow = new EventEmitter<void>();
   @Output() clickOpenWithMicrosoft = new EventEmitter<void>();
   @Output() clickOpenWithGoogle = new EventEmitter<void>();
   @Output() clickDownload = new EventEmitter<void>();

   // INFO-PANEL OUTPUTS
   @Output() saveEdittedDescription = new EventEmitter<string>();
   @Output() postNewComment = new EventEmitter<{
      content: string;
      isPostSuccess: (isSuccess: boolean) => void;
   }>();
   @Output() saveEdittedComment = new EventEmitter<{
      originalItem: ViewerInfoPanelComponent["comment"];
      edittedContent: string;
   }>();
   @Output() deleteComment = new EventEmitter<ViewerInfoPanelComponent["comment"]>();
   @Output() saveMetadataType = new EventEmitter<{
      formType: MetadataFormType;
      properties: MetadataFormProperty[];
      isSaveTypeSuccess: (isSuccess: boolean) => void;
   }>();
   private _infoPanelConfig: InfoPanelConfig;
   @Input() 
   set infoPanelConfig(config: InfoPanelConfig) {
      this._infoPanelConfig = config;
   }
   get infoPanelConfig(): InfoPanelConfig {
      return this._infoPanelConfig;
   }
   /**
    * Subscription to see if PDFTron's NotesPanel is open or closed.
    *
    * If NotesPanel is open, side panel should be closed (and vice versa).
    */
   private notesPanelVisibilitySubscription: Subscription;

   @ViewChild("infoPanel") infoPanel: ElementRef<HTMLElement>;
   @ViewChild(FileViewerComponent) fileViewer: FileViewerComponent;

   protected showSideInfo = false;
   certData: any;
   constructor(
      public service: IdexService,
      public dialog: MatDialog,
      private renderer: Renderer2,
      private annoService: AnnotationControllerService,
      private route: ActivatedRoute
   ) {}

   ngOnInit() {
      this.notesPanelVisibilitySubscription = this.annoService
         .getNotesPanelVisibilityStatus()
         .subscribe((isNotesPanelOpen) => {
            // If PDFTron's NotesPanel is open while side panel is open, hide side panel
            if (isNotesPanelOpen && this.infoPanel?.nativeElement.className.indexOf("slide-right") === -1) {
               this.closeInfoPanel();
            }
         });
         this.annoService.isPdftronOpened$().subscribe(isOpen => {
            if (!this._infoPanelConfig) return;

            this._infoPanelConfig.enableInfoPanel = !isOpen;
         });
         this.route.params.forEach((params: Params) => {
            this.loadCertification(params['id']);
         });
   }

   ngOnDestroy() {
      this.notesPanelVisibilitySubscription?.unsubscribe();
   }

   @HostListener("window:beforeunload", ["$event"])
   unload($event: any) {
      sessionStorage.setItem("shareId", this.service.shareId);
      sessionStorage.setItem("shareDocId", this.service.shareDocId);
      sessionStorage.setItem("isDownload", this.service.isDownload.toString());
      sessionStorage.setItem("permissionLevel", this.service.permissionLevel.toString());
   }

   canDeactivate() {
      // If the PDFTron viewer component is closed while there are unsaved changes,
      // ask the user if they want to save the changes.
      if (!this.fileViewer?.pdfTronComp) {
         return true;
      }
      return this.fileViewer?.pdfTronComp.handleUnsavedChangesOnExit();
   }

   //=============================================================================
   // HANDLE TOP-BAR ON-CLICK BUTTONS
   //=============================================================================

   protected onClickBack() {
      this.clickBack.emit();
   }

   protected onClickShare() {
      this.clickShare.emit();
   }

   protected oClickStartFlow() {
      this.clickStartFlow.emit();
   }

   protected onClickOpenWithMicrosoft() {
      this.clickOpenWithMicrosoft.emit();
   }

   protected onClickOpenWithGoogle() {
      this.clickOpenWithGoogle.emit();
   }

   protected onClickDownload() {
      this.clickDownload.emit();
   }

   /**
    * Open/close the info panel (side bar on the right)
    */
   protected onClickToggleInfoPanel(): void {
      if (!this.infoPanel) return;

      if (this.isInfoPanelCurrentlyOpen()) {
         this.closeInfoPanel();
      } else {
         this.openInfoPanel();
      }
   }

   //=============================================================================
   // SHOW/HIDE SIDE PANEL
   //=============================================================================

   private isInfoPanelCurrentlyOpen(): boolean {
      return this.infoPanel.nativeElement.className.indexOf("slide-right") === -1;
   }

   closeInfoPanel(): void {
      this._infoPanelConfig.enableInfoPanel = false;
      this.renderer.addClass(this.infoPanel.nativeElement, "slide-right");
   }
   openInfoPanel(): void {
      //| When DMS side panel is open,
      //| if PDFTron's NotesPanel is also open, close it.
      this._infoPanelConfig.enableInfoPanel = true;
      this.annoService.instance?.UI.closeElements(["notesPanel"]);
      this.annoService.instance?.Core.documentViewer.getAnnotationManager().deselectAllAnnotations();

      this.renderer.removeClass(this.infoPanel.nativeElement, "slide-right");
   }

   //=============================================================================
   // HANDLE INFO PANEL ON-CLICK BUTTONS
   //=============================================================================

   protected onSaveEdittedDescription($event: string): void {
      this.saveEdittedDescription.emit($event);
   }

   protected onClickPostNewComment($event: { content: string; isPostSuccess: (isSuccess: boolean) => void }): void {
      this.postNewComment.emit($event);
   }

   protected onClickSaveEdittedComment($event: {
      originalItem: ViewerInfoPanelComponent["comment"];
      edittedContent: string;
   }): void {
      this.saveEdittedComment.emit($event);
   }

   protected onClickDeleteComment($event: ViewerInfoPanelComponent["comment"]): void {
      this.deleteComment.emit($event);
   }

   //=============================================================================
   // HANDLE METADATA ON-CLICK BUTTONS
   //=============================================================================

   protected onClickSaveMetadataType($event: {
      formType: MetadataFormType;
      properties: MetadataFormProperty[];
      isSaveTypeSuccess: (isSuccess: boolean) => void;
   }): void {}

   loadCertification(id) {     
      this.service.loadCertification(id).subscribe(res => {
         if (res) {
            this.certData = res;
         }
      });
   }
}
